import { Link } from "react-router-dom";
import { useContent, lines } from "@/context/ContentContext";
import { RichText } from "@/components/RichText";
import { ArrowRight, Check, Mail } from "lucide-react";

export default function ListingOptions() {
  const c = useContent();

  const CATEGORIES = [
    { id: "double-page", name: "Double Page", key: "double" },
    { id: "full-page", name: "Full Page", key: "full" },
    { id: "half-page", name: "Half Page", key: "half" },
    { id: "normal-listing", name: "Normal Listing", key: "normal" },
  ].map((cat) => ({
    ...cat,
    title: c[`listing_${cat.key}_title`],
    desc: c[`listing_${cat.key}_desc`],
    highlights: lines(c[`listing_${cat.key}_highlights`]),
    price: c[`listing_${cat.key}_price`],
    sample: c[`listing_${cat.key}_sample`],
  }));

  return (
    <div data-testid="listing-options-page">
      <div className="bg-[#2a0a12] text-white">
        <div className="max-w-7xl mx-auto px-6 py-14">
          <h1 className="font-serif text-4xl lg:text-5xl font-bold">{c.listing_page_title}</h1>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 pt-12">
        <div className="text-slate-600" data-testid="listing-options-intro"><RichText html={c.listing_intro} /></div>
      </div>

      {CATEGORIES.map((cat, i) => (
        <section key={cat.id} id={cat.id} className={i % 2 === 1 ? "bg-white border-y border-slate-200" : ""} data-testid={`listing-section-${cat.id}`}>
          <div className="max-w-7xl mx-auto px-6 py-14">
            <div className={`grid lg:grid-cols-2 gap-10 items-start ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}>
              <div className="rounded-xl overflow-hidden border border-slate-200 shadow-lg bg-white">
                <img src={cat.sample} alt={`${cat.name} sample listing`} className="w-full" data-testid={`sample-${cat.id}`} />
                <p className="text-center text-xs text-slate-400 py-2 border-t border-slate-100">Sample {cat.name} Listing</p>
              </div>
              <div>
                <span className="inline-block text-xs uppercase tracking-[0.25em] text-[#C5A059] font-semibold mb-2">{cat.name}</span>
                <h2 className="font-serif text-3xl text-slate-900">{cat.title}</h2>
                <RichText html={cat.desc} className="mt-4 [&_p]:text-slate-600" />

                <h3 className="mt-6 text-sm font-semibold text-slate-800 uppercase tracking-wide">Key highlights of {cat.name} Listings are:</h3>
                <ul className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
                  {cat.highlights.map((h) => (
                    <li key={h} className="flex items-start gap-2.5 text-sm text-slate-700">
                      <Check className="h-4 w-4 text-[#800020] mt-0.5 shrink-0" /> {h}
                    </li>
                  ))}
                </ul>

                <div className="mt-6 inline-flex items-baseline gap-2 rounded-lg bg-[#faf6f0] border border-[#C5A059]/40 px-5 py-3">
                  <span className="text-sm text-slate-500">Annual Listing Price</span>
                  <span className="font-serif text-xl font-semibold text-[#800020]" data-testid={`price-${cat.id}`}>{cat.price}</span>
                </div>

                <div className="mt-6">
                  <Link to="/list-your-school/listing-questionnaire" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#800020] hover:bg-[#660019] text-white font-medium transition-colors" data-testid={`cta-${cat.id}`}>
                    Send us the Online Listing Questionnaire <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}

      <div className="max-w-5xl mx-auto px-6 py-14 text-center">
        <p className="text-slate-600 leading-relaxed">
          If you wish to list your School in the next edition of BSD, please fill the Online Listing Questionnaire. Various listing options are available including Double Spread Page, Full Page, Half Page and Normal Listing. Fill-in the Listing Questionnaire and send it to us along with the photographs.{" "}
          <Link to="/list-your-school/brochure-media-kit" className="text-[#800020] underline">Download the brochure</Link> for detailed information on various Listing categories.
        </p>
        <a href={`mailto:${c.contact_email}`} className="mt-5 inline-flex items-center gap-2 text-[#800020] font-medium" data-testid="listing-options-email">
          <Mail className="h-4 w-4" /> {c.contact_email}
        </a>
      </div>
    </div>
  );
}
